import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Button, FormikField as Field, GuidanceBox, H1, H3, Input, Text, VStack } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Footer from '../../../../Shared/components-v2/footer/Footer';
import { monthlyExpenses, useEmergencyFundStep } from '../FlowConfig';
import { EmergencyFundPurposeCreationOptionDocument } from '../graphql/query.emergencyFundPurposeCreationOption.generated';

type FormValues = Yup.InferType<typeof monthlyExpenses>;

export const MonthlyExpensesStep: React.FC = () => {
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();

  const { data } = useGraphQLQuery(EmergencyFundPurposeCreationOptionDocument, {
    variables: { id: 'emergency_fund:emergency_2' },
    throwOnError: true,
  });

  const { commit } = useEmergencyFundStep('monthlyExpenses');

  if (!data || data.purposeCreationOption.__typename !== 'EmergencyFundPurposeCreationOption') return null;

  const { purposeCreationOption: emergencyFundOption } = data;

  const initialValues: FormValues = {
    monthlyExpensesAmount: formatMoney(emergencyFundOption.defaultMonthlyExpenses, 'raw'),
  };

  const handleSubmit = (formValues: FormValues) => {
    commit(formValues);

    void navigate(`/add_purpose/emergency_fund/${flowId}/coverage_selection`);
  };

  return (
    <WorkflowLayout pageTitle="Monthly Expenses">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>What are your estimated monthly expenses?</H1>
            <Text>We'll use this to help you decide on a savings target for your emergency fund.</Text>
          </VStack>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={monthlyExpenses}
            validateOnChange={false}
          >
            <Form>
              <VStack css={{ gap: 'space.400' }}>
                <VStack css={{ gap: 'space.200' }}>
                  <Field name="monthlyExpensesAmount">
                    <Field.Label>Estimated monthly expenses</Field.Label>
                    <Field.Control>
                      <Input mask="number" leadingSymbol="$" trailingSymbol="/month" />
                    </Field.Control>
                    <Field.ErrorMessage />
                  </Field>
                  <GuidanceBox css={{ maxWidth: '100%' }}>
                    <GuidanceBox.Icon />
                    {emergencyFundOption.recommendation.title && (
                      <GuidanceBox.Header>
                        <H3 variant="title100">{emergencyFundOption.recommendation.title}</H3>
                      </GuidanceBox.Header>
                    )}
                    <GuidanceBox.Content css={{ display: 'grid', gap: 'space.100' }}>
                      <Text
                        variant="text50"
                        dangerouslySetInnerHTML={{ __html: emergencyFundOption.recommendation.body }}
                      />
                    </GuidanceBox.Content>
                  </GuidanceBox>
                  <Text
                    as="div"
                    color="text.weak"
                    variant="text50"
                    dangerouslySetInnerHTML={{ __html: emergencyFundOption.disclosure }}
                  />
                </VStack>

                <Button type="submit" css={{ width: 'fit-content' }}>
                  Continue
                </Button>
              </VStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
